import { useCallback, useEffect, useState } from "react";

import { PageNavigation } from "~/components/PageNavigation";
import { SelectInput } from "~/components/SelectInput";
import { listActions } from "~/modules/actions/services";
import type {
	Action,
	ActionsQueryParams,
	LogModule,
} from "~/modules/actions/types";
import type { Option } from "~/types/types";
import { debouncer } from "~/utils/debounce";
import { getLocalDate } from "~/utils/getLocalDate";

let abortController = new AbortController();
const debounce = debouncer();

const MODULE_LABELS: Record<LogModule, string> = {
	subscriptions: "Assinaturas",
	plans: "Planos",
	"subscriptions-discounts": "Descontos usados em assinaturas",
	charges: "Cobranças",
	discounts: "Descontos",
	invoices: "Faturas",
	"personal-trainers": "Personal Trainers",
	students: "Alunos",
	evaluations: "Avaliações",
	admins: "Administradores",
	"personal-trainer-licenses-changes":
		"Alterações de Licenças de Personal Trainer",
};

const logModulesOptions: Option<LogModule | "">[] = [
	{ label: "Todas", value: "" },
	{ label: "Assinaturas", value: "subscriptions" },
	{ label: "Planos", value: "plans" },
	{
		label: "Descontos usados em assinaturas",
		value: "subscriptions-discounts",
	},
	{ label: "Cobranças", value: "charges" },
	{ label: "Descontos", value: "discounts" },
	{ label: "Faturas", value: "invoices" },
	{ label: "Personal Trainers", value: "personal-trainers" },
	{ label: "Alunos", value: "students" },
	{ label: "Avaliações", value: "evaluations" },
	{ label: "Administradores", value: "admins" },
	{
		label: "Alterações de Licenças de Personal Trainer",
		value: "personal-trainer-licenses-changes",
	},
];

type FilterQuery = {
	page: number;
	size: number;
	module: LogModule | "";
};

export const Actions: React.FC = () => {
	const [rows, setRows] = useState<Action[]>([]);
	const [totalPages, setTotalPages] = useState<number>(0);
	const [filterQuery, setFilterQuery] = useState<FilterQuery>({
		page: 0,
		size: 10,
		module: "",
	});

	const handleUpdatePage = useCallback(async (page: number) => {
		setFilterQuery((state) => ({ ...state, page }));
	}, []);

	const handleUpdateModule = useCallback((module: LogModule | "") => {
		setFilterQuery((state) => ({ ...state, page: 0, module }));
	}, []);

	useEffect(() => {
		abortController = new AbortController();
		debounce(() => {
			const params: ActionsQueryParams = {
				page: filterQuery.page,
				limit: filterQuery.size,
				module: filterQuery.module || undefined,
			};
			listActions(params, abortController.signal).then((data) => {
				if (data) {
					setRows(data.actions);
					setTotalPages(data.meta.totalPages);
				}
			});
		});

		return () => {
			abortController.abort();
		};
	}, [filterQuery]);

	return (
		<div className="flex flex-col flex-1 p-5 bg-slate-100 overflow-auto">
			<div className="flex flex-col gap-4 justify-end p-5 bg-white rounded-xl overflow-hidden">
				<div>
					<SelectInput
						label="Filtrar por módulo"
						options={logModulesOptions}
						value={filterQuery.module}
						onChange={handleUpdateModule}
					/>
				</div>

				<div className="max-w-full flex-1 overflow-hidden overflow-x-auto">
					<table className="w-full min-w-[1050px]">
						<thead>
							<tr className="text-left">
								<th>Módulo</th>
								<th>Ação</th>
								<th>Texto</th>
								<th>Alvo</th>
								<th>Autor</th>
								<th>Criado em</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((a) => (
								<tr key={a.id} className="even:bg-gpa-blue-50">
									<td className="py-2 whitespace-nowrap">
										{MODULE_LABELS[a.module]}
									</td>
									<td className="py-2 whitespace-nowrap">{a.action}</td>
									<td className="py-2">{a.text ?? "-"}</td>
									<td className="py-2 whitespace-nowrap">
										{a.targetId ?? "-"}
									</td>
									<td className="py-2 whitespace-nowrap">{a.actor ?? "-"}</td>
									<td className="py-2 whitespace-nowrap">
										{getLocalDate(a.createdAt)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				<div className="self-end">
					<PageNavigation
						numPages={totalPages}
						currentPage={filterQuery.page}
						onPageChange={handleUpdatePage}
					/>
				</div>
			</div>
		</div>
	);
};
